import React, { FC, ReactNode } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ChannelGroup } from "../../../../hooks/useChannelGroups";
import { filterRealFiles } from "../../helper";

interface ChannelAccordionProps {
    group: ChannelGroup;
    defaultExpanded?: boolean;
    /** Show "delivered/total videos delivered" instead of a plain count.
     *  Only meaningful when group.videos includes empty (key-less) slots. */
    showDelivered?: boolean;
    /** Rendered right-aligned in the summary row. Injected by callers only —
     *  this component is shared with public pages, so it carries no role
     *  logic of its own. */
    headerAction?: ReactNode;
    children: ReactNode;
}

export function ChannelOptionDisplay({ selections }: { selections: any[] }) {
    const groupedByOption: Record<
        string,
        { optionName: string; type?: string; items: any[] }
    > = {};

    (selections || []).forEach((sel: any) => {
        const optionId = sel?.option_value?.option?.id;
        if (optionId == null) return;
        const key = String(optionId);
        if (!groupedByOption[key]) {
            groupedByOption[key] = {
                optionName: sel.option_value.option.name,
                type: sel.option_value.option.type,
                items: [],
            };
        }
        groupedByOption[key].items.push(sel);
    });

    return (
        <Box className="flex flex-wrap gap-x-3 gap-y-0.5">
            {Object.entries(groupedByOption).map(
                ([optionId, { optionName, type, items }]) => {
                    let displayValue = "";
                    if (type === "value") {
                        const parts = [...items]
                            .sort(
                                (a, b) =>
                                    Number(a.numeric_value) -
                                    Number(b.numeric_value)
                            )
                            .map((i) => i.numeric_value)
                            .filter(
                                (n) =>
                                    n !== null &&
                                    n !== undefined &&
                                    n !== ""
                            );
                        displayValue = parts.join(" - ");
                    } else {
                        displayValue = items
                            .map((i) => i.option_value?.name)
                            .filter((n) => n != null && n !== "")
                            .join(", ");
                    }
                    if (!optionName || !displayValue) return null;
                    return (
                        <Typography
                            key={optionId}
                            sx={{
                                fontFamily: "CircularStd",
                                fontWeight: 450,
                                fontSize: "12px",
                                color: "var(--muted)",
                            }}
                        >
                            <span style={{ color: "var(--text-2)" }}>
                                {optionName}:
                            </span>{" "}
                            {displayValue}
                        </Typography>
                    );
                }
            )}
        </Box>
    );
}

export const ChannelAccordion: FC<ChannelAccordionProps> = ({
    group,
    defaultExpanded = false,
    showDelivered = false,
    headerAction,
    children,
}) => {
    const selections = group.channel.project_option_selections || [];
    const totalCount = group.videos.length;
    const deliveredCount = filterRealFiles(group.videos).length;

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            sx={{
                boxShadow: "none",
                border: "1px solid var(--border)",
                borderRadius: "12px !important",
                mb: 1.5,
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: "0 0 12px 0" },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                    borderRadius: "12px",
                    minHeight: "56px",
                    "&.Mui-expanded": { minHeight: "56px" },
                    "& .MuiAccordionSummary-content": { margin: "12px 0" },
                    "& .MuiAccordionSummary-content.Mui-expanded": {
                        margin: "12px 0",
                    },
                }}
            >
                <Box className="flex flex-col gap-1">
                    <Box className="flex items-center gap-3">
                        <Typography
                            sx={{
                                fontFamily: "CircularStd",
                                fontWeight: 700,
                                fontSize: "15px",
                                color: "var(--text)",
                            }}
                        >
                            {group.channel.name}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "CircularStd",
                                fontWeight: 450,
                                fontSize: "13px",
                                color: "var(--text-2)",
                            }}
                        >
                            {showDelivered
                                ? `${deliveredCount}/${totalCount} video${totalCount !== 1 ? "s" : ""} delivered`
                                : `${totalCount} video${totalCount !== 1 ? "s" : ""}`}
                        </Typography>
                    </Box>
                    {selections.length > 0 && (
                        <ChannelOptionDisplay selections={selections} />
                    )}
                </Box>
                {headerAction && (
                    <Box
                        className="flex items-center ml-auto mr-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {headerAction}
                    </Box>
                )}
            </AccordionSummary>
            <AccordionDetails sx={{ p: "20px" }}>{children}</AccordionDetails>
        </Accordion>
    );
};
